import { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Tag, List, Space, Avatar, Drawer, Descriptions, Timeline, Empty, Select, Button, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FileTextOutlined, InboxOutlined, PictureOutlined, RightOutlined, TagOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../../services/api';
import './ArtifactsPage.css';

const { Title, Paragraph, Text } = Typography;

const stageLabelMap = {
  design: '设计阶段', development: '开发阶段', testing: '测试阶段', deployment: '部署阶段',
} as const;

const stageAccentMap = {
  design: { border: '#1677ff', soft: '#eff6ff', shadow: 'rgba(22,119,255,.18)' },
  development: { border: '#13a8a8', soft: '#eefcfb', shadow: 'rgba(19,168,168,.18)' },
  testing: { border: '#fa8c16', soft: '#fff7e8', shadow: 'rgba(250,140,22,.18)' },
  deployment: { border: '#722ed1', soft: '#f7f0ff', shadow: 'rgba(114,46,209,.18)' },
} as const;

const PHASES = ['design', 'development', 'testing', 'deployment'] as const;

interface Artifact {
  id: string; stage: 'design'|'development'|'testing'|'deployment';
  type: string; format: string; name: string; versionNo: string;
  current: boolean; status: string; testPassed: boolean;
  summary: string; content: string; updatedAt: string;
  meta?: {label:string;value:string}[];
  deploymentUrl?: string;
}

// 用服务器外网IP，不走localhost；生产环境通过环境变量配置
const EXT_IP = import.meta.env.VITE_EXT_IP || window.location.hostname;

function buildContent(m: any, workspacePath?: string): string {
  const phase = m.phase || 'development';
  const deploymentUrl = m.artifacts?.deploymentUrl || m.artifacts?.url;
  if (phase === 'deployment' && deploymentUrl) {
    return [
      `## 版本: ${m.version}`,
      `## 阶段: 部署阶段`,
      `## 创建时间: ${new Date(m.createdAt).toLocaleString('zh-CN')}`,
      ``,
      `### 项目访问地址`,
      deploymentUrl,
      ``,
      `说明: 点击页面右侧的访问按钮可在浏览器新窗口打开。`,
    ].join(String.fromCharCode(10));
  }
  if (phase === 'development' && workspacePath) {
    const lines = [
      `## 版本: ${m.version}`,
      `## 阶段: 开发阶段`,
      `## 创建时间: ${new Date(m.createdAt).toLocaleString('zh-CN')}`,
      ``,
      `### Web前端入口`,
      `访问地址: http://${EXT_IP}:5347`,
      `(如端口有变更请以实际部署为准)`,
      ``,
      `### 工作区路径`,
      `${workspacePath}`,
    ];
    return lines.join('\n');
  }
  return `## 版本: ${m.version}\n## 阶段: ${stageLabelMap[phase] || phase}\n## 创建时间: ${new Date(m.createdAt).toLocaleString('zh-CN')}\n\n${m.notes || ''}`;
}

export default function ArtifactsPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [selectedStage, setSelectedStage] = useState<'design'|'development'|'testing'|'deployment'>('design');
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // Load all projects, then filter to only those with milestones
  useEffect(() => {
    api.get('/projects').then(async r => {
      const all = (r.data||[]).filter((p:any)=>!p.deletedAt);
      setAllProjects(all);
      // 检查哪些项目有里程碑
      const withMilestones: any[] = [];
      for (const p of all) {
        try {
          const mr = await api.get(`/projects/${p.id}/milestones`);
          if ((mr.data||[]).length > 0) withMilestones.push(p);
        } catch {}
      }
      setProjects(withMilestones);
    }).catch(()=>{});
  }, []);

  // Load milestones → artifacts
  const currentProjectId = projectId || projects[0]?.id;
  const currentProject = allProjects.find((p:any)=>p.id===currentProjectId);
  useEffect(() => {
    if (!currentProjectId) return;
    setLoading(true);
    api.get(`/projects/${currentProjectId}/milestones`).then(r => {
      const ms = r.data || [];
      const wp = currentProject?.workspacePath || '';
      const arts: Artifact[] = ms.map((m:any) => {
        const deploymentUrl = m.artifacts?.deploymentUrl || m.artifacts?.url;
        const isDeploymentUrl = ((m.phase as any) || 'development') === 'deployment' && !!deploymentUrl;
        return {
        id: m.id, stage: (m.phase as any) || 'development',
        type: isDeploymentUrl ? 'link' : 'package', format: isDeploymentUrl ? 'URL' : '版本', name: isDeploymentUrl ? `🔗 项目访问入口` : `📌 ${m.version}`,
        versionNo: m.version, current: false, status: 'confirmed',
        testPassed: true, summary: isDeploymentUrl ? deploymentUrl : (m.notes || `版本 ${m.version}`),
        content: buildContent(m, wp),
        updatedAt: new Date(m.createdAt).toLocaleString('zh-CN'),
        meta: [{label:'版本号',value:m.version},{label:'阶段',value:stageLabelMap[m.phase]||m.phase}, ...(deploymentUrl ? [{label:'访问地址', value: deploymentUrl}] : [])],
        deploymentUrl,
      };
      });
      PHASES.forEach(phase => {
        const phaseArts = arts.filter(a=>a.stage===phase).sort((a,b)=>b.versionNo.localeCompare(a.versionNo));
        if (phaseArts[0]) phaseArts[0].current = true;
      });
      setArtifacts(arts);
    }).catch(()=>{setArtifacts([]);}).finally(()=>setLoading(false));
  }, [currentProjectId]);

  // 总是显示4个阶段，即使没有产物
  const stageCards = useMemo(() => PHASES.map(stage => {
    const versions = artifacts.filter(a=>a.stage===stage).sort((a,b)=>b.versionNo.localeCompare(a.versionNo));
    const current = versions[0] || null;
    return { stage, current, versions };
  }), [artifacts]);

  const selectedStageCard = stageCards.find(s=>s.stage===selectedStage)!;
  const selectedArtifact = (selectedStageCard?.versions||[]).find(a=>a.id===selectedArtifactId)||selectedStageCard?.versions[0]||null;
  const versionOptions = (selectedStageCard?.versions||[]).map(a=>({label:`${a.versionNo}${a.current?'（当前版）':''}`,value:a.id}));

  useEffect(() => {
    const card = stageCards.find(s=>s.stage===selectedStage)!;
    if (!card.versions.length) { setSelectedArtifactId(null); return; }
    if (!card.versions.some(a=>a.id===selectedArtifactId)) setSelectedArtifactId((card.versions.find(a=>a.current)||card.versions[0])?.id||null);
  }, [selectedStage]);

  const getIcon = (type:string) => type==='link'?<LinkOutlined/>:type==='document'?<FileTextOutlined/>:type==='image'?<PictureOutlined/>:type==='package'?<InboxOutlined/>:<TagOutlined/>;
  const getStatusColor = (s:string) => s==='ready'?'success':s==='confirmed'?'processing':'default';
  const projectOptions = projects.map(p=>({label:p.name,value:p.id}));

  if (!projects.length) return <Empty style={{marginTop:60}} description="暂无有产物的项目"/>;

  return (
    <div className='art-page' style={{width:'100%'}}>
      <Space direction="vertical" size={20} style={{width:'100%'}}>
        <Card className='lg-glass-panel'>
          <Space direction="vertical" size={12} style={{width:'100%'}}>
            <Space wrap style={{justifyContent:'space-between',width:'100%'}}>
              <div>
                <Title level={3} className='lg-title-gradient' style={{margin:0}}>产物中心</Title>
                <Paragraph type="secondary" style={{marginTop:8,marginBottom:0}}>
                  版本里程碑 · 阶段产物 · 部署包。所有产物由AI全流程页面「里程碑」按钮自动生成。
                </Paragraph>
              </div>
              <Select value={currentProjectId} style={{width:320,height:44,fontSize:16,borderRadius:14,fontWeight:600}}
                options={projectOptions} onChange={(v)=>navigate(`/artifacts/${v}`)} className="art-project-select"/>
            </Space>
          </Space>
        </Card>

        <Row gutter={[20,20]}>
          <Col xs={24} xl={8}>
            <Card title="产物清单" className='lg-glass-card' style={{height:'100%'}}>
              {loading ? <Spin style={{display:'block',margin:'40px auto'}}/> :
                <List itemLayout="vertical" dataSource={stageCards} renderItem={(item) => (
                  <List.Item key={item.stage} style={{paddingLeft:0,paddingRight:0,borderBottom:'none'}}
                    onClick={()=>{setSelectedStage(item.stage);setSelectedArtifactId((item.versions.find(a=>a.current)||item.versions[0])?.id||null);}}>
                    <div className={`art-stage-item ${selectedStage===item.stage ? 'art-stage-item-active' : ''}`} style={{cursor:'pointer',borderRadius:18,
                      border:`1.5px solid ${selectedStage===item.stage?stageAccentMap[item.stage].border:'#e8eef8'}`,
                      background:selectedStage===item.stage?`linear-gradient(180deg,${stageAccentMap[item.stage].soft} 0%,#ffffff 100%)`:'#ffffff',
                      boxShadow:selectedStage===item.stage?`0 14px 32px ${stageAccentMap[item.stage].shadow}`:'0 6px 18px rgba(15,23,42,.04)',
                      padding:16,transition:'all .2s ease'}}>
                      <List.Item.Meta avatar={<Avatar icon={getIcon('package')} className='art-stage-avatar' style={{background:stageAccentMap[item.stage].border}}/>}
                        title={<Space wrap><span style={{fontWeight:700}}>{stageLabelMap[item.stage]}</span>
                          <Tag color={selectedStage===item.stage?'blue':'default'}>{selectedStage===item.stage?'已选中':'点击查看'}</Tag>
                          {item.current?.versionNo&&<Tag color="geekblue">当前 {item.current.versionNo}</Tag>}</Space>}
                        description={item.current ? `${item.current.format} · ${item.current.updatedAt} · 共 ${item.versions.length} 个版本` : '暂无产物'}/>
                      {item.current ? (
                        <Space wrap style={{marginBottom:8}}>
                          <Tag color={getStatusColor(item.current.status)}>{item.current.status==='ready'?'已就绪':item.current.status==='confirmed'?'已确认':'草稿'}</Tag>
                        </Space>
                      ) : null}
                      {item.current ? <Text type="secondary">{item.current.summary}</Text> : <Text type="secondary" style={{fontStyle:'italic'}}>尚未生成，请前往AI全流程创建</Text>}
                    </div>
                  </List.Item>
                )}/>
              }
            </Card>
          </Col>

          <Col xs={24} xl={16}>
            <Card title={selectedStageCard ? `${stageLabelMap[selectedStageCard.stage]}详情` : '产物详情'}
              extra={selectedArtifact?<Button type="link" onClick={()=>setDetailOpen(true)}>全屏查看</Button>:null}
              className='lg-glass-card' style={{minHeight:520}}>
              {selectedArtifact ? (
                <Space direction="vertical" size={16} style={{width:'100%'}}>
                  <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="产物名称">{selectedArtifact.name}</Descriptions.Item>
                    <Descriptions.Item label="所属阶段">{stageLabelMap[selectedArtifact.stage]}</Descriptions.Item>
                    <Descriptions.Item label="当前查看版本">
                      <Select value={selectedArtifact.id} style={{width:'100%'}} options={versionOptions} onChange={(v)=>setSelectedArtifactId(v)}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="更新时间">{selectedArtifact.updatedAt}</Descriptions.Item>
                    <Descriptions.Item label="格式">{selectedArtifact.format}</Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Space wrap>{selectedArtifact.versionNo&&<Tag color="geekblue">{selectedArtifact.versionNo}</Tag>}
                        {selectedArtifact.current&&<Tag color="cyan">当前版</Tag>}
                        <Tag color={getStatusColor(selectedArtifact.status)}>{selectedArtifact.status==='ready'?'已就绪':selectedArtifact.status==='confirmed'?'已确认':'草稿'}</Tag></Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="摘要" span={2}>{selectedArtifact.summary}</Descriptions.Item>
                  </Descriptions>
                  {selectedArtifact.meta?.length?<Card size="small" title="附加信息" className="art-info-card">
                    <Descriptions size="small" column={2}>{selectedArtifact.meta.map(m=><Descriptions.Item key={m.label} label={m.label}>{m.value}</Descriptions.Item>)}</Descriptions></Card>:null}
                  {selectedArtifact.deploymentUrl ? (
                    <Card size="small" title="部署访问入口" className="art-deploy-card">
                      <Space direction="vertical" size={10} style={{width:'100%'}}>
                        <a href={selectedArtifact.deploymentUrl} target="_blank" rel="noreferrer" style={{fontSize:20,fontWeight:800,color:'#0958d9',wordBreak:'break-all'}}><LinkOutlined/> {selectedArtifact.deploymentUrl}</a>
                        <Button type="primary" icon={<LinkOutlined/>} onClick={()=>window.open(selectedArtifact.deploymentUrl, '_blank', 'noopener,noreferrer')}>打开项目页面</Button>
                      </Space>
                    </Card>
                  ) : null}
                  <Card size="small" title="内容预览" className="art-preview-card">
                    <pre style={{whiteSpace:'pre-wrap',margin:0,fontFamily:'Consolas,monospace',lineHeight:1.7,maxHeight:'52vh',overflow:'auto'}}>{selectedArtifact.content}</pre>
                  </Card>
                </Space>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                  <Space direction="vertical" size={4}>
                    <span>当前阶段暂无产物</span>
                    <Button type="link" onClick={()=>navigate(`/dev-workspace?projectId=${currentProjectId}`)}>前往AI全流程创建</Button>
                  </Space>
                }/>
              )}
            </Card>
          </Col>
        </Row>

        <Drawer title={selectedArtifact?.name||'产物详情'} placement="right" width="72vw" open={detailOpen} onClose={()=>setDetailOpen(false)}>
          {selectedArtifact?<Card size="small" title="全屏查看">
            <pre style={{whiteSpace:'pre-wrap',margin:0,fontFamily:'Consolas,monospace',lineHeight:1.7}}>{selectedArtifact.content}</pre>
          </Card>:null}
        </Drawer>

        <div onClick={()=>setTimelineOpen(true)} className='art-timeline-trigger'
          style={{position:'fixed',right:0,top:'40%',zIndex:1000,width:28,height:96,
            background:'linear-gradient(180deg,#1677ff 0%,#6f42c1 100%)',color:'#fff',
            borderTopLeftRadius:16,borderBottomLeftRadius:16,display:'flex',alignItems:'center',justifyContent:'center',
            cursor:'pointer',boxShadow:'0 8px 28px rgba(22,119,255,.25)',backdropFilter:'blur(12px)'}}>
          <RightOutlined/>
        </div>

        <Drawer title="产物流转路径" placement="right" width={420} open={timelineOpen} onClose={()=>setTimelineOpen(false)}>
          <Paragraph type="secondary">版本里程碑按时间顺序展示，每个版本记录所属阶段和创建时间。</Paragraph>
          <Timeline items={[...artifacts].sort((a,b)=>a.versionNo.localeCompare(b.versionNo)).map(a=>({
            color:'blue', children:<div><Space wrap><Text strong>{a.versionNo}</Text><Tag>{stageLabelMap[a.stage]}</Tag></Space><div style={{color:'#64748b',marginTop:6}}>{a.summary}</div><div style={{fontSize:11,color:'#94a3b8'}}>{a.updatedAt}</div></div>
          }))}/>
        </Drawer>
      </Space>
    </div>
  );
}
